"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

interface ApplicationItem {
	titleKey: string;
	descKey: string;
}

interface ImageItem {
	src: string;
	labelKey: string;
	href?: string;
}

export const DEFAULT_LEFT_APPLICATIONS: ApplicationItem[] = [
	{ titleKey: "app1Title", descKey: "app1Desc" },
	{ titleKey: "app2Title", descKey: "app2Desc" },
	{ titleKey: "app3Title", descKey: "app3Desc" },
];

export const DEFAULT_CENTER_APPLICATIONS: ApplicationItem[] = [
	{ titleKey: "app4Title", descKey: "app4Desc" },
	{ titleKey: "app5Title", descKey: "app5Desc" },
	{ titleKey: "app6Title", descKey: "app6Desc" },
];

export const FOUR_LEFT_APPLICATIONS: ApplicationItem[] = [
	{ titleKey: "app1Title", descKey: "app1Desc" },
	{ titleKey: "app2Title", descKey: "app2Desc" },
	{ titleKey: "app3Title", descKey: "app3Desc" },
	{ titleKey: "app4Title", descKey: "app4Desc" },
];

export const THREE_CENTER_APPLICATIONS: ApplicationItem[] = [
	{ titleKey: "app5Title", descKey: "app5Desc" },
	{ titleKey: "app6Title", descKey: "app6Desc" },
	{ titleKey: "app7Title", descKey: "app7Desc" },
];

const DEFAULT_IMAGES: ImageItem[] = [
	{
		src: "/image/new-version/machining/five-axis/app/aerospace-components.webp",
		labelKey: "imageLabel1",
		href: "/industry/aerospace",
	},
	{
		src: "/image/new-version/machining/five-axis/app/automotive-motorsport-parts.webp",
		labelKey: "imageLabel2",
		href: "/industry/automotive",
	},
	{
		src: "/image/new-version/machining/five-axis/app/die-mold-tooling.webp",
		labelKey: "imageLabel3",
		href: "/industry/industry-equipment",
	},
	{
		src: "/image/new-version/machining/five-axis/app/medical-implants-devices.webp",
		labelKey: "imageLabel4",
		href: "/industry/medical-device",
	},
	{
		src: "/image/new-version/machining/five-axis/app/marine-naval-components.webp",
		labelKey: "imageLabel5",
		href: "/industry/robotics-automation",
	},
	{
		src: "/image/new-version/machining/five-axis/app/energy-sector-equipment.webp",
		labelKey: "imageLabel6",
		href: "/industry/consumer-electronics",
	},
];

function ApplicationImageCard({ image, label }: { image: ImageItem; label: string }) {
	const card = (
		<>
			<div className="relative h-16 w-16 overflow-hidden rounded-full sm:h-20 sm:w-20">
				<Image src={image.src} alt={label} fill className="object-cover" sizes="80px" />
			</div>
			<span className="mt-4 self-end text-right text-sm font-semibold text-black sm:text-base">{label}</span>
		</>
	);

	const className =
		"group flex self-start flex-col rounded-xl bg-[#ededed] p-4 transition-colors duration-300 hover:bg-themePrimary400";

	if (image.href) {
		return (
			<Link href={image.href} className={className} aria-label={label}>
				{card}
			</Link>
		);
	}

	return <div className={className}>{card}</div>;
}

export interface ApplicationProps {
	translationNamespace?: string;
	leftApplications?: ApplicationItem[];
	centerApplications?: ApplicationItem[];
	images?: ImageItem[];
	layout?: "three-column" | "header-top";
}

export default function Application({
	translationNamespace = "cncMillingApplication",
	leftApplications = FOUR_LEFT_APPLICATIONS,
	centerApplications = THREE_CENTER_APPLICATIONS,
	images = DEFAULT_IMAGES,
	layout = "header-top",
}: ApplicationProps) {
	const t = useTranslations(translationNamespace);

	if (layout === "three-column") {
		return (
			<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
				<div className="container">
					<div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
						<span className="mb-3 inline-block text-xl uppercase tracking-wider text-themePrimary400 max-md:text-lg">
							{t("subtitle")}
						</span>

						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{t("title")}
						</h2>

						<p className="text-sm leading-relaxed text-black md:text-base">{t("description")}</p>
					</div>

					<div className="grid gap-8 lg:grid-cols-3 lg:gap-10">
						<div className="space-y-8">
							{leftApplications.map((app, index) => (
								<div key={index}>
									<h3 className="mb-2 text-base font-semibold text-mainColor sm:text-lg">
										{t(app.titleKey)}
									</h3>
									<p className="text-sm leading-relaxed text-black md:text-base">{t(app.descKey)}</p>
								</div>
							))}
						</div>

						<div className="space-y-8">
							{centerApplications.map((app, index) => (
								<div key={index}>
									<h3 className="mb-2 text-base font-semibold text-mainColor sm:text-lg">
										{t(app.titleKey)}
									</h3>
									<p className="text-sm leading-relaxed text-black md:text-base">{t(app.descKey)}</p>
								</div>
							))}
						</div>

						<div className="grid auto-rows-min grid-cols-2 items-start gap-3 self-center">
							{images.map((image, index) => (
								<ApplicationImageCard key={index} image={image} label={t(image.labelKey)} />
							))}
						</div>
					</div>
				</div>
			</section>
		);
	}

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
					<span className="mb-3 inline-block text-xl uppercase tracking-wider text-themePrimary400 max-md:text-lg">
						{t("subtitle")}
					</span>

					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>

					<p className="text-sm leading-relaxed text-black md:text-base">{t("description")}</p>
				</div>

				<div className="grid gap-8 lg:grid-cols-3 lg:gap-10">
					{(() => {
						const allApps = [...leftApplications, ...centerApplications];
						const isOdd = allApps.length % 2 !== 0;
						const lastIndex = allApps.length - 1;

						return (
							<div className="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-2 lg:col-span-2">
								{allApps.map((app, index) => (
									<div key={index} className={isOdd && index === lastIndex ? "md:col-span-2" : ""}>
										<h3 className="mb-2 text-base font-semibold text-themePrimary400 sm:text-lg">
											{t(app.titleKey)}
										</h3>
										<p className="text-sm leading-relaxed text-black md:text-base">{t(app.descKey)}</p>
									</div>
								))}
							</div>
						);
					})()}

					<div className="grid auto-rows-min grid-cols-2 items-start gap-3 self-center">
						{images.map((image, index) => (
							<ApplicationImageCard key={index} image={image} label={t(image.labelKey)} />
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
